/**
 * Maintenance-watch warnings on an equipment assignment (api#944, API-4).
 *
 * ─── WARN, NEVER BLOCK ──────────────────────────────────────────────────────
 *
 * These are advice. The assignment is written whatever they say: the API's
 * status code and writes are unchanged by them, and nothing here may disable a
 * Save or gate a selection on them. The one refusal on these doors is still the
 * field add's 409 `cert_warning` (lib/cert-warnings.ts), which is a different
 * thing and is not touched by this file.
 *
 * ─── THREE SPELLINGS, ONE FACT ──────────────────────────────────────────────
 *
 * Read from the live handlers, not from the brief (which named the field door
 * `maintenanceWarnings`; it is snake_case):
 *
 *   GET   /dispatch/jobs/:id/available-equipment  row.maintenance_warnings[]  { flag_type, severity, message }
 *   PATCH /dispatch/jobs/:id/equipment            data.maintenanceWarnings[]  { equipmentId, flagType, severity, message }
 *   POST  /employee/my-jobs/:id/equipment         data.maintenance_warnings[] { equipment_id, flag_type, severity, message }
 *
 * ─── ABSENT MEANS "NOT SHOWN", AND RENDERS NOTHING ──────────────────────────
 *
 * In `shadow` and `off` the API computes nothing for the client and sends NO
 * key. That is not "checked and clear", so no screen may render an all-clear
 * line from it — an absent key and an empty array both render nothing.
 *
 * The `message` is the API's sentence and is rendered verbatim; it names the
 * asset, so a post-save notice can quote it without a lookup.
 */

export interface AssignmentMaintenanceWarning {
  /** Absent on the picker row, where the row IS the asset. */
  equipmentId: string | null;
  flagType: string;
  severity: string;
  message: string;
}

const isObj = (v: unknown): v is Record<string, any> => !!v && typeof v === 'object';

function normalise(raw: unknown): AssignmentMaintenanceWarning | null {
  if (!isObj(raw) || typeof raw.message !== 'string' || raw.message.trim() === '') return null;
  return {
    equipmentId: raw.equipmentId ?? raw.equipment_id ?? null,
    flagType: raw.flagType ?? raw.flag_type ?? '',
    severity: raw.severity ?? '',
    message: raw.message,
  };
}

function list(raw: unknown): AssignmentMaintenanceWarning[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalise).filter((w): w is AssignmentMaintenanceWarning => w !== null);
}

/** A picker row's warnings. No key (shadow/off) → []. */
export function pickerMaintenanceWarnings(row: unknown): AssignmentMaintenanceWarning[] {
  return isObj(row) ? list(row.maintenance_warnings) : [];
}

/**
 * A save response's warnings, from either door. Takes the axios `res.data`
 * envelope. `'data' in body` rather than `body.data || body`: a null `data`
 * must not fall through to the envelope.
 */
export function savedMaintenanceWarnings(body: unknown): AssignmentMaintenanceWarning[] {
  if (!isObj(body)) return [];
  const inner = 'data' in body ? body.data : body;
  if (!isObj(inner)) return [];
  return list(inner.maintenanceWarnings ?? inner.maintenance_warnings);
}

/** The post-save notice title. Never a success line — the save worked, but it carries advice. */
export function maintenanceNoticeTitle(warnings: AssignmentMaintenanceWarning[]): string {
  const n = warnings.length;
  return `Assigned. ${n} maintenance ${n === 1 ? 'check' : 'checks'} on this equipment.`;
}
